import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { type IconType, Pill, PillSkeleton } from "@src/appV2/redesign/system/ui-components";

import { WorkplaceHighlightType } from "./constants";
import { type WorkplaceHighlight } from "./types";

export interface WorkplaceHighlightPillsProps {
  highlights: WorkplaceHighlight[];
  isLoading?: boolean;
  wrapperSx?: SxProps<Theme>;
}

const HIGHLIGHT_ORDER: WorkplaceHighlightType[] = [
  WorkplaceHighlightType.QUALIFICATION_PATIENTS_PER_WORKER,
  WorkplaceHighlightType.CANCELLATION_PAYOUT,
  WorkplaceHighlightType.EXTRA_TIME_PAY,
  WorkplaceHighlightType.WELL_SUPPLIED,
  WorkplaceHighlightType.CHARTING_SOFTWARE,
  WorkplaceHighlightType.DRESS_CODE,
  WorkplaceHighlightType.BREAKS,
  WorkplaceHighlightType.INDEPENDENT_CONTRACTOR_BADGE_REQUIRED,
  WorkplaceHighlightType.CANCELLATION_PROTECTION,
  WorkplaceHighlightType.LATE_CLOCK_OUT,
];

const iconTypeByHighlightType: Record<WorkplaceHighlightType, IconType> = {
  [WorkplaceHighlightType.AM_PATIENTS_PER_WORKER]: "sun-outlined",
  [WorkplaceHighlightType.PM_PATIENTS_PER_WORKER]: "sunset-outlined",
  [WorkplaceHighlightType.NOC_PATIENTS_PER_WORKER]: "moon-outlined",
  [WorkplaceHighlightType.QUALIFICATION_PATIENTS_PER_WORKER]: "patients",
  [WorkplaceHighlightType.CANCELLATION_PAYOUT]: "extra-pay",
  [WorkplaceHighlightType.CHARTING_SOFTWARE]: "software",
  [WorkplaceHighlightType.DRESS_CODE]: "dress-code",
  [WorkplaceHighlightType.EXTRA_TIME_PAY]: "extra-pay",
  [WorkplaceHighlightType.BREAKS]: "break",
  [WorkplaceHighlightType.WELL_SUPPLIED]: "supplies",
  [WorkplaceHighlightType.INDEPENDENT_CONTRACTOR_BADGE_REQUIRED]: "document-id-outlined",
  [WorkplaceHighlightType.CANCELLATION_PROTECTION]: "parachute",
  [WorkplaceHighlightType.LATE_CLOCK_OUT]: "calendar-clock",
  [WorkplaceHighlightType.HIRING]: "briefcase",
};

type Intent = "neutral" | "success" | "warning" | "error" | "disabled";

const iconIntentByHighlightType = new Map<WorkplaceHighlightType, Intent>([
  [WorkplaceHighlightType.HIRING, "success"],
]);

export function WorkplaceHighlightPills(props: WorkplaceHighlightPillsProps) {
  const { highlights, isLoading, wrapperSx } = props;

  if (isLoading) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={3}>
        <PillSkeleton width="30%" />
        <PillSkeleton width="50%" />
        <PillSkeleton width="20%" />
        <PillSkeleton width="35%" />
        <PillSkeleton width="35%" />
        <PillSkeleton width="40%" />
        <PillSkeleton width="40%" />
      </Stack>
    );
  }

  const sortedHighlights = [...highlights].sort(
    (a, b) => HIGHLIGHT_ORDER.indexOf(a.type) - HIGHLIGHT_ORDER.indexOf(b.type)
  );

  return (
    <Stack direction="row" flexWrap="wrap" gap={3} sx={wrapperSx}>
      {sortedHighlights
        // Doesn't make sense to display highlights without a label.
        // Should never happen, but it's here just to be safe.
        .filter((highlight) => highlight.label)
        .map((highlight) => {
          const intent = iconIntentByHighlightType.get(highlight.type);
          const colorProps = isDefined(intent)
            ? {
                color: (theme: Theme) => theme.palette.intent?.[intent]?.text,
                iconColor: (theme: Theme) => theme.palette.intent?.[intent]?.icon,
                backgroundColor: (theme: Theme) => theme.palette.intent?.[intent]?.background,
              }
            : undefined;

          return (
            <Pill
              key={highlight.type}
              multiline
              size="medium"
              label={highlight.label}
              iconType={iconTypeByHighlightType[highlight.type]}
              {...colorProps}
              sx={{ width: "max-content" }}
            />
          );
        })}
    </Stack>
  );
}
